import { debounce } from "lodash";
import { HTMLProps, useCallback } from "react";

import { ContentEditor } from "components/ContentEditor/ContentEditor";
import { lexicalToDraftJS } from "components/ContentEditor/converter/contentFormatConverter";
import { convertDraftJSToHtml } from "components/ContentEditor/editorUtils";
import { BaseField } from "components/organisms/form/BaseField";
import { FormElementError, useForm } from "components/organisms/form/Form";
import { useRefState } from "hooks/useRefState";

import { ContentEditorData } from "generated/graphql-types";

export type ContentEditorValue = {
	json?: string;
	html?: string;
};
type TextFieldProps = { onChange?: (value: ContentEditorValue) => void } & Omit<HTMLProps<HTMLInputElement>, "onChange">;

export default function ContentEditorField({ name, value, defaultValue, onChange, ...props }: TextFieldProps) {
	const { errors, defaultValue: formDefaultValue } = useForm(name, {
		getValue: () => value || valueRef.current,
	});
	const [valueRef, setValue] = useRefState<ContentEditorData>((defaultValue as ContentEditorValue) || formDefaultValue || "");

	const debouncedFunction = debounce((json: string) => {
		const draftJsFormat = lexicalToDraftJS(json);
		const html = convertDraftJSToHtml(draftJsFormat);
		const value = {
			json: JSON.stringify(draftJsFormat),
			html,
		};
		setValue(value);
		onChange?.(value);
	}, 300);

	const debouncedOnChange = useCallback(
		(json: string) => {
			debouncedFunction(json);
		},
		[debouncedFunction]
	);

	const json = (defaultValue as ContentEditorValue)?.json || (formDefaultValue as ContentEditorValue)?.json || undefined;
	return (
		<BaseField errors={errors as FormElementError<unknown>}>
			<>
				{props.label && <div style={{ marginBottom: "8px" }}>{props.label}</div>}
				<ContentEditor defaultValue={json} onChange={debouncedOnChange} />
			</>
		</BaseField>
	);
}
